import { Injectable } from '@angular/core';
import { DEFAULT_PROMO_PAGINATION_PARAMS } from '@my-account/const';
import {
  ForemanPromocodesApiService,
  MyAccountActions,
  MyAccountSelectors,
  StateWithMyAccount,
} from '@my-account/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { AuthActions } from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';

@Injectable()
export class ForemanPromocodesEffects {
  loadBenefitSplitLimits$: Observable<
    MyAccountActions.LoadBenefitSplitLimitsSuccess | MyAccountActions.LoadBenefitSplitLimitsFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(MyAccountActions.LOAD_BENEFIT_SPLIT_LIMITS),
      switchMap(() =>
        this.foremanPromocodesApiService.loadBenefitSplitLimits().pipe(
          map((limits) => new MyAccountActions.LoadBenefitSplitLimitsSuccess(limits)),
          catchError(() => of(new MyAccountActions.LoadBenefitSplitLimitsFail())),
        ),
      ),
    ),
  );

  loadPromocodes$: Observable<
    MyAccountActions.LoadForemanPromocodesSuccess | MyAccountActions.LoadForemanPromocodesFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType<MyAccountActions.LoadForemanPromocodes>(MyAccountActions.LOAD_FOREMAN_PROMOCODES),
      switchMap((action) =>
        this.foremanPromocodesApiService.loadPromocodes(action.payload).pipe(
          map((response) => new MyAccountActions.LoadForemanPromocodesSuccess({ ...response, params: action.payload })),
          catchError(() => of(new MyAccountActions.LoadForemanPromocodesFail())),
        ),
      ),
    ),
  );

  generatePromocode$: Observable<MyAccountActions.GeneratePromocodeSuccess | MyAccountActions.GeneratePromocodeFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType<MyAccountActions.GeneratePromocode>(MyAccountActions.GENERATE_PROMOCODE),
        concatMap((action) =>
          this.foremanPromocodesApiService.generatePromocode(action.payload).pipe(
            map((promocode) => new MyAccountActions.GeneratePromocodeSuccess(promocode)),
            catchError(() => of(new MyAccountActions.GeneratePromocodeFail())),
          ),
        ),
      ),
    );

  reloadPromocodesAfterGenerate$: Observable<MyAccountActions.LoadForemanPromocodes> = createEffect(() =>
    this.actions$.pipe(
      ofType(MyAccountActions.GENERATE_PROMOCODE_SUCCESS),
      concatLatestFrom(() => this.store.select(MyAccountSelectors.getForemanPromocodesParams)),
      map(([, params]) => new MyAccountActions.LoadForemanPromocodes(params ?? DEFAULT_PROMO_PAGINATION_PARAMS)),
    ),
  );

  resetForemanPromocodesOnLogout$: Observable<
    | MyAccountActions.LoadBenefitSplitLimitsReset
    | MyAccountActions.GeneratePromocodeReset
    | MyAccountActions.LoadForemanPromocodesReset
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.LOGOUT),
      switchMap(() => [
        new MyAccountActions.LoadBenefitSplitLimitsReset(),
        new MyAccountActions.GeneratePromocodeReset(),
        new MyAccountActions.LoadForemanPromocodesReset(),
      ]),
    ),
  );

  constructor(
    private actions$: Actions,
    private foremanPromocodesApiService: ForemanPromocodesApiService,
    private store: Store<StateWithMyAccount>,
  ) {}
}
